import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "../RootStore";
import { CourtBookingApi, getMatchClutchStats } from "../../apis/bookingapi";
import {
  MatchClutchStatsResponse,
  MatchResponsePayload,
  MatchUserList,
  PlayerDetails,
  Score,
  UserModel,
} from "../../apis/types";
import { Colors } from "../../utils/colors";

export class TeamScore {
  teamId: number;
  playerA: PlayerDetails;
  playerB: PlayerDetails;
  scores: Score[];
  noOfSetsWon: number;
  constructor(
    teamId: number,
    playerA: PlayerDetails,
    playerB: PlayerDetails,
    scores: Score[],
    noOfSetsWon: number
  ) {
    this.teamId = teamId;
    this.playerA = playerA;
    this.playerB = playerB;
    this.scores = scores;
    this.noOfSetsWon = noOfSetsWon;
  }
}

export class MatchDetailsStore {
  rootStore: RootStore;
  matchId: string = "";
  modalData?: {
    visible: boolean;
    title: string;
    children: string;
    btnText?: string;
    btnVariant?: string;
    onClose?: () => void;
    onAccept?: () => void;
    headerIcon?: React.ReactNode;
  };
  loading: boolean = false;
  matchDetails: MatchResponsePayload | null = null;
  teamScoreA?: TeamScore;
  teamScoreB?: TeamScore;
  colorMap: Map<number, string> = new Map();
  matchClutchStats?: MatchClutchStatsResponse;
  teamMates: MatchUserList[] = [];
  opponents: MatchUserList[] = [];
  partnerComparisonCurrentPage: number = 0;
  currentPartnerComparisonPlayers: MatchUserList[] = [];
  currentComparisonTab: string = "pair";
  isRefreshing: boolean = false;
  matchDate: string = "";
  clutchMatchCurrentPlayer: MatchUserList = {};
  userModel: UserModel | null = null;
  isPromoMode: boolean = false;
  matchGuid?: string = undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // reaction to matchId to get matchDetails
    reaction(
      () => this.matchId,
      async (id) => {
        if (id) {
          this.startLoading();
          try {
            const response = await CourtBookingApi.getMatchDetails(
              parseInt(id)
            );
            this.matchDetails = response;
          } catch (error: any) {
            console.log("Error: ", error);
            this.modalData = {
              visible: true,
              title: "Error",
              children:
                error?.data?.message ??
                error.message ??
                "An error occurred while fetching match details",
              onAccept: () => {
                this.modalData = undefined;
              },
            };
          } finally {
            this.stopLoading();
          }
        }
      }
    );

    // reaction to matchId to get matchClutchStats
    reaction(
      () => this.matchId,
      async (id) => {
        if (id) {
          try {
            const response = await getMatchClutchStats(parseInt(id));
            this.matchClutchStats = response;
            this.rootStore.matchStatsStore.setMatchClutchStatsResponse(
              response
            );
          } catch (error: any) {
            console.log("Error: ", error);
          }
        }
      }
    );

    // reaction to userModel to set teamMates and opponents
    reaction(
      () => ({
        userModel: this.userModel,
        matchDetails: this.matchDetails,
      }),
      ({ userModel, matchDetails }) => {
        if (userModel && matchDetails) {
          // Check if userModel id matches with teamA playerA or playerB userId
          const { teamA, teamB } = matchDetails;
          if (
            userModel.userId === teamA.playerA.playerId.toString() ||
            userModel.userId === teamA.playerB.playerId.toString()
          ) {
            // user is in teamA
            this.setTeammates([
              {
                userId: teamA.playerA.playerId.toString(),
                name: teamA.playerA.playerName,
                userImageUrl: teamA.playerA.playerImageUrl,
                teamId: teamA.id,
              },
              {
                userId: teamA.playerB.playerId.toString(),
                name: teamA.playerB.playerName,
                userImageUrl: teamA.playerB.playerImageUrl,
                teamId: teamA.id,
              },
            ]);
            this.setOpponents([
              {
                userId: teamB.playerA.playerId.toString(),
                name: teamB.playerA.playerName,
                userImageUrl: teamB.playerA.playerImageUrl,
                teamId: teamB.id,
              },
              {
                userId: teamB.playerB.playerId.toString(),
                name: teamB.playerB.playerName,
                userImageUrl: teamB.playerB.playerImageUrl,
                teamId: teamB.id,
              },
            ]);
          } else if (
            userModel.userId === teamB.playerA.playerId.toString() ||
            userModel.userId === teamB.playerB.playerId.toString()
          ) {
            // user is in teamB
            this.setTeammates([
              {
                userId: teamB.playerA.playerId.toString(),
                name: teamB.playerA.playerName,
                userImageUrl: teamB.playerA.playerImageUrl,
                teamId: teamB.id,
              },
              {
                userId: teamB.playerB.playerId.toString(),
                name: teamB.playerB.playerName,
                userImageUrl: teamB.playerB.playerImageUrl,
                teamId: teamB.id,
              },
            ]);
            this.setOpponents([
              {
                userId: teamA.playerA.playerId.toString(),
                name: teamA.playerA.playerName,
                userImageUrl: teamA.playerA.playerImageUrl,
                teamId: teamA.id,
              },
              {
                userId: teamA.playerB.playerId.toString(),
                name: teamA.playerB.playerName,
                userImageUrl: teamA.playerB.playerImageUrl,
                teamId: teamA.id,
              },
            ]);
          }
        }
      }
    );

    // reaction to matchDetails to calculate scores for each set for both teams
    reaction(
      () => this.matchDetails,
      (matchDetails) => {
        if (matchDetails) {
          // Calculate scores for each set for both teams
          const teamAScores = matchDetails.scores
            .filter((score) => score.teamId === matchDetails.teamA.id)
            .sort((a, b) => a.setNumber - b.setNumber);
          // const teamBScores = matchDetails.scores
          //   .filter((score) => score.teamId === matchDetails.teamB.id)
          //   .sort((a, b) => a.setNumber - b.setNumber);
          const teamBScores = matchDetails.scores
            .filter((score) => score.teamId !== matchDetails.teamA.id)
            .sort((a, b) => a.setNumber - b.setNumber);

          // Compare scores set by set to determine number of sets won
          let teamAWonSets = 0;
          let teamBWonSets = 0;
          for (
            let i = 0;
            i < Math.max(teamAScores.length, teamBScores.length);
            i++
          ) {
            const aScore = teamAScores[i]?.score ?? -1;
            const bScore = teamBScores[i]?.score ?? -1;
            if (aScore > bScore) {
              teamAWonSets++;
            } else if (bScore > aScore) {
              teamBWonSets++;
            }
          }

          this.teamScoreA = new TeamScore(
            matchDetails.teamA.id,
            matchDetails.teamA.playerA,
            matchDetails.teamA.playerB,
            teamAScores,
            teamAWonSets
          );
          this.teamScoreB = new TeamScore(
            matchDetails.teamB.id,
            matchDetails.teamB.playerA,
            matchDetails.teamB.playerB,
            teamBScores,
            teamBWonSets
          );
        }
      }
    );

    // Map colors to players when teams are set
    reaction(
      () => ({
        teamMateLength: this.teamMates.length,
        opponentLength: this.opponents.length,
      }),
      () => {
        if (this.teamMates.length > 0 && this.opponents.length > 0) {
          this.mapColorToPlayers();
        }
      }
    );

    // Update current comparison players
    reaction(
      () => ({
        currentPartnerPage: this.partnerComparisonCurrentPage,
        teamMateLength: this.teamMates.length,
        opponentLength: this.opponents.length,
      }),
      () => {
        if (this.partnerComparisonCurrentPage === 0) {
          this.currentPartnerComparisonPlayers = this.teamMates;
        } else {
          this.currentPartnerComparisonPlayers = this.opponents;
        }
      }
    );

    // Handle refresh
    reaction(
      () => ({
        refresh: this.isRefreshing,
      }),
      () => {
        if (this.isRefreshing) {
          this.reset();
          this.rootStore.matchStatsStore.reset();
        }
      }
    );

    reaction(
      () => this.matchGuid,
      async (matchGuid) => {
        if (matchGuid) {
          this.setIsPromoMode(true);
          this.startLoading();
          try {
            const response = await CourtBookingApi.getDemoMatchDetails(
              matchGuid
            );
            this.matchDetails = response;
            this.setUserModel({
              name: response.teamA.playerA.playerName ?? "",
              mobile: "",
              email: "",
              address: {
                id: 0,
                street: "",
                street2: "",
                zip: 0,
                stateCode: "",
                countryCode: "",
                city: "",
                countryName: "",
                stateName: "",
              },
              userId: response.teamA.playerA.playerId?.toString() ?? "",
              authToken: "",
              guid: "",
              selectedClubId: 0,
              userRating: 0,
              userImageUrl: response.teamA.playerA?.playerImageUrl ?? "",
              activePaymentMethodOnFile: true,
              confirmDataRequired: true,
              gender: 0,
              totalMatchCount: 0,
              role: 0,
              eWalletBalance: 0,
            });
            const clutchResponse =
              await CourtBookingApi.getDemoMatchClutchStatus(matchGuid);
            // mapping from match playerIds → resPartnerIds
            const playerResPartnerMap: Record<number, number> = {
              65934: response.teamA.playerA.playerId,
              72090: response.teamA.playerB.playerId,
              112965: response.teamB.playerA.playerId,
              66466: response.teamB.playerB.playerId,
            };

            // mutate clutch response and inject correct resPartnerIds
            const mutatedResponse: MatchClutchStatsResponse = {
              ...clutchResponse,
              apiPlayerDetectedShots:
                clutchResponse.apiPlayerDetectedShots?.map((data, index) => {
                  return {
                    ...data,
                    individualStats: data.individualStats?.map((stat) => ({
                      ...stat,
                      resPartnerId:
                        playerResPartnerMap[stat.resPartnerId] ??
                        stat.resPartnerId,
                    })),
                  };
                }),
            };

            this.matchClutchStats = mutatedResponse;

            this.rootStore.matchStatsStore.setMatchClutchStatsResponse(
              mutatedResponse
            );
          } catch (error: any) {
            console.log("Error: ", error);
            this.modalData = {
              visible: true,
              title: "Error",
              children:
                error?.data?.message ??
                error.message ??
                "An error occurred while fetching match details",
              onAccept: () => {
                this.modalData = undefined;
              },
            };
          } finally {
            this.stopLoading();
          }
        }
      }
    );
  }

  setUserModel(userModel: UserModel) {
    this.userModel = userModel;
  }

  setIsPromoMode = (value: boolean) => {
    this.isPromoMode = value;
  };

  mapColorToPlayers = () => {
    this.colorMap.clear();
    this.colorMap.set(parseInt(this.teamMates[0]?.userId ?? "0"), Colors.gold);
    this.colorMap.set(
      parseInt(this.teamMates[1]?.userId ?? "0"),
      Colors.success
    );
    this.colorMap.set(
      parseInt(this.opponents[0]?.userId ?? "0"),
      Colors.darkBrown
    );
    this.colorMap.set(parseInt(this.opponents[1]?.userId ?? "0"), Colors.pink);
  };

  setClutchMatchCurrentPlayer = (clutchMatchCurrentPlayer: MatchUserList) => {
    this.clutchMatchCurrentPlayer = clutchMatchCurrentPlayer;
    // Share current player with stats store
    this.rootStore.matchStatsStore.setClutchMatchCurrentPlayer(
      clutchMatchCurrentPlayer
    );
  };

  setTeammates = (teammates: MatchUserList[]) => {
    this.teamMates = teammates;
    this.setClutchMatchCurrentPlayer(this.teamMates[0]);
    // Share teammates with stats store
    this.rootStore.matchStatsStore.setTeammates(teammates);
  };

  setOpponents = (opponents: MatchUserList[]) => {
    this.opponents = opponents;
    // Share opponents with stats store
    this.rootStore.matchStatsStore.setOpponents(opponents);
  };

  setPartnerComparisonCurrentPage = (partnerComparisonCurrentPage: number) => {
    this.partnerComparisonCurrentPage = partnerComparisonCurrentPage;
    this.rootStore.matchStatsStore.setPartnerComparisonCurrentPage(
      partnerComparisonCurrentPage
    );
  };

  setCurrentComparisonTab = (currentComparisonTab: string) => {
    this.currentComparisonTab = currentComparisonTab;
    if (this.partnerComparisonCurrentPage > 0) {
      this.partnerComparisonCurrentPage = 0;
    }
  };

  startLoading = () => {
    this.loading = true;
  };

  stopLoading = () => {
    this.loading = false;
  };

  setMatchId = (matchId: string) => {
    this.matchId = matchId;
  };

  setMatchGuid = (id: string) => {
    this.matchGuid = id;
  };

  setRefresh = (isRefreshing: boolean) => {
    this.isRefreshing = isRefreshing;
  };

  setMatchDate = (matchDate: string) => {
    this.matchDate = matchDate;
  };

  reset() {
    this.matchClutchStats = undefined;
    this.clutchMatchCurrentPlayer = {};
    this.teamMates = [];
    this.opponents = [];
    this.colorMap.clear();
    this.partnerComparisonCurrentPage = 0;
    this.currentPartnerComparisonPlayers = [];
    this.currentComparisonTab = "pair";
    this.loading = false;
    this.matchId = "";
    this.isRefreshing = false;
    this.matchDate = "";
    this.isPromoMode = false;
    this.matchGuid = undefined;
  }
}
